import React from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ModeIcon from '@mui/icons-material/Mode';
import {
  Box,
  Checkbox,
  FormControl,
  IconButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
} from '@mui/material';

import { type MasterInclusion, type PackageOption, TABLE_GRID_COLUMNS } from './utils';

interface Props {
  inclusion: MasterInclusion;
  packageOptions: Array<PackageOption>;
  vendorId?: string;
  onChange: (id: string, nextValues: Partial<MasterInclusion>) => void;
}

const PACKAGE_SELECT_MENU_PROPS = {
  PaperProps: {
    sx: {
      maxHeight: 360,
      mt: 0.5,
      '& .MuiMenuItem-root': {
        alignItems: 'flex-start',
        gap: 1,
        py: 1,
        whiteSpace: 'normal',
      },
      '& .MuiMenuItem-root.Mui-selected': {
        bgcolor: 'background.paper',
      },
      '& .MuiMenuItem-root.Mui-selected:hover': {
        bgcolor: 'action.hover',
      },
      '& .MuiListItemText-primary': {
        whiteSpace: 'normal',
      },
    },
  },
};

function getAssignedPackagesLabel(selectedPackageIds: Array<string>, packageOptions: Array<PackageOption>) {
  const packageOptionIds = new Set(packageOptions.map((option) => option.id));
  const validPackageIds = selectedPackageIds.filter((packageId) => packageOptionIds.has(packageId));

  if (packageOptions.length === 0) {
    return 'No packages available';
  }

  if (validPackageIds.length === 0) {
    return 'No packages';
  }

  if (validPackageIds.length === packageOptions.length) {
    return 'All packages';
  }

  if (validPackageIds.length === 1) {
    return packageOptions.find((option) => option.id === validPackageIds[0])?.name ?? '1 package';
  }

  return `${validPackageIds.length} of ${packageOptions.length} packages`;
}

export default function InclusionRow({ inclusion, packageOptions, vendorId, onChange }: Props) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: inclusion.id });
  const editInclusionUrl =
    vendorId && inclusion.persistId ? `/vendors/${vendorId}/inclusions?inclusionId=${inclusion.persistId}` : undefined;

  return (
    <Box
      ref={setNodeRef}
      sx={{
        alignItems: 'center',
        bgcolor: 'background.paper',
        borderTop: 1,
        borderColor: 'divider',
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: '1fr', lg: TABLE_GRID_COLUMNS },
        opacity: isDragging ? 0.7 : 1,
        px: 2,
        py: 2,
        transform: CSS.Transform.toString(transform),
        transition,
        '& .MuiInputBase-root': {
          bgcolor: 'background.paper',
        },
      }}
    >
      <IconButton
        size="small"
        aria-label={`Reorder ${inclusion.inclusion || 'inclusion'}`}
        sx={{ color: 'text.disabled', cursor: 'grab' }}
        {...attributes}
        {...listeners}
      >
        <DragIndicatorIcon fontSize="small" />
      </IconButton>

      <TextField value={inclusion.category} size="small" fullWidth slotProps={{ input: { readOnly: true } }} />

      <TextField value={inclusion.inclusion} size="small" fullWidth slotProps={{ input: { readOnly: true } }} />

      <Stack direction="row" justifyContent={{ xs: 'flex-start', lg: 'center' }}>
        <Switch
          size="small"
          checked={inclusion.highlighted}
          onChange={(event) =>
            onChange(inclusion.id, {
              highlighted: event.target.checked,
              luxPlusOnly: event.target.checked ? false : inclusion.luxPlusOnly,
            })
          }
          disabled={inclusion.luxPlusOnly}
        />
      </Stack>

      <Stack direction="row" justifyContent={{ xs: 'flex-start', lg: 'center' }}>
        <Switch
          size="small"
          checked={inclusion.luxPlusOnly}
          onChange={(event) =>
            onChange(inclusion.id, {
              highlighted: event.target.checked ? false : inclusion.highlighted,
              luxPlusOnly: event.target.checked,
            })
          }
          disabled={inclusion.highlighted}
        />
      </Stack>

      <FormControl size="small" fullWidth>
        <Select
          multiple
          value={inclusion.assignedPackageIds}
          displayEmpty
          renderValue={(selected) => getAssignedPackagesLabel(selected, packageOptions)}
          MenuProps={PACKAGE_SELECT_MENU_PROPS}
          onChange={(event) => {
            const value = event.target.value;
            const assignedPackageIds = Array.from(new Set(typeof value === 'string' ? value.split(',') : value));

            onChange(inclusion.id, { assignedPackageIds });
          }}
        >
          {packageOptions.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              <Checkbox size="small" checked={inclusion.assignedPackageIds.includes(option.id)} sx={{ p: 0.25 }} />
              <ListItemText primary={option.name} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <IconButton
        color="primary"
        aria-label="Edit inclusion"
        href={editInclusionUrl}
        target="_blank"
        rel="noreferrer"
        disabled={!editInclusionUrl}
      >
        <ModeIcon />
      </IconButton>
    </Box>
  );
}
